/**
 * MatchAccountLedgerPage — per-account match ledger.
 *
 * Read-only view of a Match account's ledger rows, as produced by the split
 * engine. Each row represents a contribution to this account from a match
 * entry posted against some source account (possibly this same account in
 * the case of a remainder row).
 *
 * Mirrors the shape of `PartyLedgerPage` but adapted for the
 * `MatchLedgerRow` shape (snake_case, no running balance from the server).
 *
 * Features:
 *   - Date-range filter (default: last 60 days).
 *   - Label contains-filter, client-side.
 *   - Part-kind toggle: splits only / remainder only / all.
 *   - Reversal rows: rendered with ↺ icon, strikethrough amount, muted colour.
 *   - Remainder rows: label column shows "— (remainder)" in italic.
 *   - Source account resolution: mapped client-side from the active
 *     accounts list; rows whose source equals the viewing account show
 *     "(this account)" for clarity.
 *   - Running total: cumulative sum computed client-side in reverse (since
 *     the server returns rows DESC). Starts from the current balance and
 *     subtracts back upward so each row shows the balance as of that row.
 *
 * Route: /match/accounts/:id/ledger
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  Filter,
  RotateCcw,
  X,
} from 'lucide-react';
import { match } from '../api';
import type {
  MatchAccount,
  MatchAccountDetail,
  MatchLedgerRow,
} from '../api';
import { MatchShell } from './MatchShell';
import {
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Pill,
  Spinner,
} from '../ui/primitives';

type PartKindFilter = 'all' | 'split' | 'remainder';

export function MatchAccountLedgerPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);

  const today = useMemo(() => todayIso(), []);
  const defaultFrom = useMemo(() => addDaysIso(today, -60), [today]);

  const [from, setFrom] = useState<string>(defaultFrom);
  const [to, setTo] = useState<string>(today);
  const [labelFilter, setLabelFilter] = useState<string>('');
  const [partKind, setPartKind] = useState<PartKindFilter>('all');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState<boolean>(false);

  const validId = Number.isFinite(id) && id > 0;

  const accountQuery = useQuery<MatchAccountDetail>({
    queryKey: ['match-account', id],
    queryFn: () => match.getAccount(id),
    enabled: validId,
  });

  const ledgerQuery = useQuery<{ rows: MatchLedgerRow[] }>({
    queryKey: ['match-ledger', id, from, to],
    queryFn: () => match.getLedger(id, { from, to, limit: 500 }),
    enabled: validId && !!from && !!to,
  });

  const accountsQuery = useQuery<MatchAccount[]>({
    queryKey: ['match-accounts-all'],
    queryFn: () => match.listAccounts({ status: 'all' }),
  });

  const accountsById = useMemo(() => {
    const m = new Map<number, MatchAccount>();
    (accountsQuery.data ?? []).forEach((a) => m.set(a.id, a));
    return m;
  }, [accountsQuery.data]);

  // Apply client-side filters (label contains, part-kind).
  const filteredRows = useMemo(() => {
    const rows = ledgerQuery.data?.rows ?? [];
    const needle = labelFilter.trim().toLowerCase();
    return rows.filter((r) => {
      if (partKind !== 'all' && r.part_kind !== partKind) return false;
      if (needle.length > 0) {
        const hay = (r.label ?? '').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [ledgerQuery.data, labelFilter, partKind]);

  // Running total: since rows are DESC-sorted (newest first) and we have
  // the current balance, the balance AS-OF each row (after that row posted)
  // is simply the starting-from-current running total, subtracting each
  // row's amount as we walk further into the past.
  const runningTotalByRowId = useMemo(() => {
    const balStr = accountQuery.data?.balance;
    const rows = ledgerQuery.data?.rows ?? [];
    const map = new Map<number, string>();
    if (!balStr || rows.length === 0) return map;
    const cur = parseDecimalString(balStr);
    if (cur === null) return map;
    let acc = cur;
    for (const r of rows) {
      map.set(r.id, formatSignedKinr(acc));
      const amt = parseDecimalString(r.amount) ?? 0;
      acc = round3(acc - amt);
    }
    return map;
  }, [accountQuery.data?.balance, ledgerQuery.data]);

  const resetFilters = (): void => {
    setFrom(defaultFrom);
    setTo(today);
    setLabelFilter('');
    setPartKind('all');
  };

  if (!validId) {
    return (
      <MatchShell>
        <Card>
          <ErrorBanner>Not a valid Match account id.</ErrorBanner>
        </Card>
      </MatchShell>
    );
  }

  const account = accountQuery.data?.account;
  const balanceStr = accountQuery.data?.balance;
  const isLoading = accountQuery.isLoading || ledgerQuery.isLoading;
  const ledgerError = ledgerQuery.error as Error | null;
  const accountError = accountQuery.error as Error | null;

  return (
    <MatchShell>
      <div className="space-y-4">
        {/* ---------------- Header ---------------- */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <Link
              to={`/match/accounts/${id}`}
              className="inline-flex items-center gap-1 text-caption text-ink-muted hover:text-ink"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
              Back to account
            </Link>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-h1 font-semibold text-ink sm:text-2xl">
                {account?.name ?? '…'}
              </h1>
              {account?.status === 'archived' && (
                <Pill tone="amber">archived</Pill>
              )}
            </div>
            <p className="text-caption text-ink-muted">
              Match ledger — rows produced by the split engine
            </p>
          </div>
          <Card className="min-w-[160px] shrink-0">
            <div className="text-caption uppercase tracking-wide text-ink-muted">
              Current balance
            </div>
            <div className="mt-1 font-mono text-xl font-semibold text-ink sm:text-2xl">
              {balanceStr !== undefined
                ? formatKinrDisplay(balanceStr)
                : '…'}
            </div>
            <div className="mt-1 text-[11px] text-ink-muted">K-INR</div>
          </Card>
        </div>

        {accountError && (
          <ErrorBanner>
            Failed to load account: {accountError.message}
          </ErrorBanner>
        )}

        {/* ---------------- Filters (desktop) ---------------- */}
        <Card className="hidden md:block">
          <FilterControls
            from={from}
            to={to}
            labelFilter={labelFilter}
            partKind={partKind}
            onFromChange={setFrom}
            onToChange={setTo}
            onLabelFilterChange={setLabelFilter}
            onPartKindChange={setPartKind}
            onReset={resetFilters}
          />
        </Card>

        {/* ---------------- Filters (mobile trigger) ---------------- */}
        <div className="flex items-center justify-between gap-2 md:hidden">
          <button
            type="button"
            onClick={() => setMobileFiltersOpen(true)}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2 text-small text-ink hover:bg-surface-raised"
          >
            <Filter className="h-4 w-4" strokeWidth={2} />
            Filters
            {activeFilterCount(from, to, defaultFrom, today, labelFilter, partKind) > 0 && (
              <Pill tone="indigo">
                {activeFilterCount(from, to, defaultFrom, today, labelFilter, partKind)}
              </Pill>
            )}
          </button>
          <span className="text-caption text-ink-muted">
            {filteredRows.length} row{filteredRows.length === 1 ? '' : 's'}
          </span>
        </div>

        {/* ---------------- Ledger body ---------------- */}
        {ledgerError && (
          <ErrorBanner>
            Failed to load ledger: {ledgerError.message}
          </ErrorBanner>
        )}

        <Card>
          {isLoading ? (
            <div className="flex items-center gap-2 py-6 text-small text-ink-muted">
              <Spinner />
              Loading ledger…
            </div>
          ) : filteredRows.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title="No ledger rows yet"
              description={
                ledgerQuery.data && (ledgerQuery.data.rows.length ?? 0) > 0
                  ? 'No rows match your current filters.'
                  : 'Rows will appear here once an entry is posted against this account or one of its sources.'
              }
              action={
                <Link to="/match/entry">
                  <Button size="sm">Create an entry</Button>
                </Link>
              }
            />
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full text-xs">
                  <thead className="bg-surface-raised text-left text-[10px] uppercase tracking-wide text-ink-muted">
                    <tr>
                      <th className="px-2 py-1.5">Date</th>
                      <th className="px-2 py-1.5">Entry #</th>
                      <th className="px-2 py-1.5">Source account</th>
                      <th className="px-2 py-1.5">Label</th>
                      <th className="px-2 py-1.5">Part</th>
                      <th className="px-2 py-1.5 text-right">% used</th>
                      <th className="px-2 py-1.5 text-right">Amount</th>
                      <th className="px-2 py-1.5 text-right">Running</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredRows.map((row) => (
                      <LedgerDesktopRow
                        key={row.id}
                        row={row}
                        viewingAccountId={id}
                        sourceName={sourceDisplay(
                          row.source_match_account_id,
                          id,
                          accountsById,
                        )}
                        running={runningTotalByRowId.get(row.id) ?? ''}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="space-y-2 md:hidden">
                {filteredRows.map((row) => (
                  <LedgerMobileCard
                    key={row.id}
                    row={row}
                    viewingAccountId={id}
                    sourceName={sourceDisplay(
                      row.source_match_account_id,
                      id,
                      accountsById,
                    )}
                    running={runningTotalByRowId.get(row.id) ?? ''}
                  />
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Mobile filters drawer */}
      {mobileFiltersOpen && (
        <div
          className="fixed inset-0 z-40 flex items-end bg-ink/60 md:hidden"
          onClick={() => setMobileFiltersOpen(false)}
        >
          <div
            className="w-full rounded-t-2xl border-t border-border bg-surface p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-h2 font-semibold text-ink">Filters</h2>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                aria-label="Close filters"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-ink hover:bg-surface-raised"
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
            <FilterControls
              stackLayout
              from={from}
              to={to}
              labelFilter={labelFilter}
              partKind={partKind}
              onFromChange={setFrom}
              onToChange={setTo}
              onLabelFilterChange={setLabelFilter}
              onPartKindChange={setPartKind}
              onReset={resetFilters}
            />
            <div className="mt-4 flex justify-end">
              <Button size="sm" onClick={() => setMobileFiltersOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </MatchShell>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function FilterControls({
  from,
  to,
  labelFilter,
  partKind,
  onFromChange,
  onToChange,
  onLabelFilterChange,
  onPartKindChange,
  onReset,
  stackLayout = false,
}: {
  from: string;
  to: string;
  labelFilter: string;
  partKind: PartKindFilter;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  onLabelFilterChange: (v: string) => void;
  onPartKindChange: (v: PartKindFilter) => void;
  onReset: () => void;
  stackLayout?: boolean;
}) {
  const wrapClass = stackLayout
    ? 'flex flex-col gap-3'
    : 'flex flex-wrap items-end gap-x-4 gap-y-3';
  return (
    <div className={wrapClass}>
      <label className="flex flex-col gap-1 text-caption text-ink-muted">
        <span>From</span>
        <input
          type="date"
          value={from}
          onChange={(e) => onFromChange(e.target.value)}
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-small text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
        />
      </label>
      <label className="flex flex-col gap-1 text-caption text-ink-muted">
        <span>To</span>
        <input
          type="date"
          value={to}
          onChange={(e) => onToChange(e.target.value)}
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-small text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
        />
      </label>
      <label className="flex flex-col gap-1 text-caption text-ink-muted sm:min-w-[200px]">
        <span>Label contains</span>
        <input
          type="text"
          value={labelFilter}
          onChange={(e) => onLabelFilterChange(e.target.value)}
          placeholder="e.g. commission"
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-small text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
        />
      </label>
      <div className="flex flex-col gap-1 text-caption text-ink-muted">
        <span>Part kind</span>
        <div className="inline-flex overflow-hidden rounded-md border border-border bg-surface">
          {(['all', 'split', 'remainder'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => onPartKindChange(k)}
              className={
                'px-3 py-1.5 text-small capitalize transition-colors ' +
                (partKind === k
                  ? 'bg-brand text-white'
                  : 'text-ink hover:bg-surface-raised')
              }
            >
              {k === 'all' ? 'All' : k === 'split' ? 'Splits only' : 'Remainder only'}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center gap-1 self-start rounded-md border border-border px-2.5 py-1.5 text-small text-ink hover:bg-surface-raised"
      >
        <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
        Reset filters
      </button>
    </div>
  );
}

function LedgerDesktopRow({
  row,
  viewingAccountId,
  sourceName,
  running,
}: {
  row: MatchLedgerRow;
  viewingAccountId: number;
  sourceName: string;
  running: string;
}) {
  const isReversal = row.reverses_entry_id !== null;
  const isRemainder = row.part_kind === 'remainder';
  const isSelfSource = row.source_match_account_id === viewingAccountId;
  const amountDisplay = formatKinrDisplay(row.amount);
  const pct =
    row.percentage_used !== null && row.percentage_used !== ''
      ? `${row.percentage_used}%`
      : '—';
  return (
    <tr className={isReversal ? 'text-ink-muted' : 'text-ink'}>
      <td className="px-2 py-1.5 whitespace-nowrap">{row.effective_date}</td>
      <td className="px-2 py-1.5 font-mono text-[11px]">
        <Link
          to={`/match/entries/${row.match_entry_id}`}
          className="text-brand hover:underline"
        >
          #{row.match_entry_id}
        </Link>
      </td>
      <td className="px-2 py-1.5">
        <span className={isSelfSource ? 'italic text-ink-muted' : ''}>
          {sourceName}
        </span>
      </td>
      <td className="px-2 py-1.5">
        {isRemainder ? (
          <span className="italic text-ink-muted">— (remainder)</span>
        ) : (
          row.label ?? <span className="text-ink-muted">—</span>
        )}
      </td>
      <td className="px-2 py-1.5">
        <Pill tone={isRemainder ? 'amber' : 'neutral'}>{row.part_kind}</Pill>
      </td>
      <td className="px-2 py-1.5 text-right font-mono">{pct}</td>
      <td className="px-2 py-1.5 text-right font-mono">
        {isReversal ? (
          <span className="inline-flex items-center gap-1 text-ink-muted">
            <span aria-hidden>↺</span>
            <span className="line-through">{amountDisplay}</span>
          </span>
        ) : (
          amountDisplay
        )}
      </td>
      <td className="px-2 py-1.5 text-right font-mono text-ink-muted">
        {running}
      </td>
    </tr>
  );
}

function LedgerMobileCard({
  row,
  viewingAccountId,
  sourceName,
  running,
}: {
  row: MatchLedgerRow;
  viewingAccountId: number;
  sourceName: string;
  running: string;
}) {
  const isReversal = row.reverses_entry_id !== null;
  const isRemainder = row.part_kind === 'remainder';
  const isSelfSource = row.source_match_account_id === viewingAccountId;
  const amountDisplay = formatKinrDisplay(row.amount);
  return (
    <div
      className={
        'rounded-lg border p-3 text-small ' +
        (isReversal
          ? 'border-border/40 bg-surface/40 text-ink-muted'
          : 'border-border bg-surface')
      }
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-ink">
              {row.effective_date}
            </span>
            <Link
              to={`/match/entries/${row.match_entry_id}`}
              className="font-mono text-[11px] text-brand hover:underline"
            >
              #{row.match_entry_id}
            </Link>
          </div>
          <div className="truncate text-caption text-ink-muted">
            <span className={isSelfSource ? 'italic' : ''}>{sourceName}</span>
          </div>
        </div>
        <div className="shrink-0 text-right font-mono">
          {isReversal ? (
            <span className="inline-flex items-center gap-1 text-ink-muted">
              <span aria-hidden>↺</span>
              <span className="line-through">{amountDisplay}</span>
            </span>
          ) : (
            <span className="font-semibold text-ink">
              {amountDisplay}
            </span>
          )}
          {running && (
            <div className="mt-0.5 text-[11px] text-ink-muted">
              bal {running}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-caption">
        <Pill tone={isRemainder ? 'amber' : 'neutral'}>{row.part_kind}</Pill>
        {isRemainder ? (
          <span className="italic text-ink-muted">— (remainder)</span>
        ) : row.label ? (
          <span className="text-ink">{row.label}</span>
        ) : null}
        {row.percentage_used !== null && row.percentage_used !== '' && (
          <span className="text-ink-muted">{row.percentage_used}%</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function sourceDisplay(
  sourceId: number,
  viewingId: number,
  accountsById: Map<number, MatchAccount>,
): string {
  if (sourceId === viewingId) return '(this account)';
  const acc = accountsById.get(sourceId);
  if (acc) return acc.name;
  return `#${sourceId}`;
}

/**
 * Format a decimal string for display. The backend stores K-INR amounts
 * as raw Decimal strings like "-1234.567"; render them with thousand
 * separators and a visible sign for negatives.
 */
function formatKinrDisplay(raw: string): string {
  const n = parseDecimalString(raw);
  if (n === null) return raw;
  return formatSignedKinr(n);
}

function formatSignedKinr(n: number): string {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const fixed = abs.toFixed(3);
  const [intPart, fracPart] = fixed.split('.');
  const withCommas = (intPart ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${withCommas}.${fracPart ?? '000'}`;
}

function parseDecimalString(raw: string): number | null {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getUTCFullYear().toString().padStart(4, '0');
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = d.getUTCDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysIso(isoDate: string, delta: number): string {
  const parts = isoDate.split('-').map((s) => parseInt(s, 10));
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (y === undefined || m === undefined || d === undefined) return isoDate;
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + delta);
  const yy = date.getUTCFullYear().toString().padStart(4, '0');
  const mm = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const dd = date.getUTCDate().toString().padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function activeFilterCount(
  from: string,
  to: string,
  defaultFrom: string,
  defaultTo: string,
  labelFilter: string,
  partKind: PartKindFilter,
): number {
  let n = 0;
  if (from !== defaultFrom) n++;
  if (to !== defaultTo) n++;
  if (labelFilter.trim() !== '') n++;
  if (partKind !== 'all') n++;
  return n;
}
